import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import dayjs from 'dayjs';
import { dateChecker } from 'helpers/dateChecker';
import { sortChatsByLastMessage } from 'helpers/sortChats';
import { chatDataType, IChat, IChatMessage, IChatPayload, messageStatusType } from 'interfaces';
import { RootState } from 'store';
import {
  setAttemptCount,
  setChatData,
  setCheckingMessages,
  setGlobalChats,
  setRoomAllChats,
  setSelectedChatRoom,
  setUnviewedAmount,
  setUploadChatsController,
  setViewsMessagesIds,
} from 'store/slices/chatsSlice';
import { setDocuments, setSelectedDocument } from 'store/slices/dataDocumentsSlice';
import { setRooms } from 'store/slices/userDataSlice';
import { needPlayChatNotification, setSoundNotificationState } from '../slices/windowStateSlice';
import aiAssistant from 'api/ai_assistant';

type createChatPayloadType = { data: IChatPayload; ownerId: string; oponentName?: string, isAI?: boolean };
enum StatusActions {
  ADD = 'add',
  REPLACE = 'replace',
  REJECTED = 'rejected',
}

const getCheckedChat = async (
  { data: mock, type }: { data: IChatMessage; type: 'replace' | 'add' | 'cancel' },
  chatData: IChatMessage[],
  status?: messageStatusType
) => {
  const data = { ...mock, timestamp: mock.created_at };

  if (type === 'add') {
    return [...chatData, data];
  }

  if (type === 'cancel') {
    return chatData.filter(({ id }) => id !== data.id);
  }

  let attachment_ids: string[] = [];

  if(data.attachment_ids && data.attachment_ids?.length > 0){
    attachment_ids = await Promise.all(
      data.attachment_ids.map(async (id: string) => {
        const fileUrl = URL.createObjectURL((await api.getAttachment(id)).data);
        return fileUrl;
      })
    ).catch((err) => (attachment_ids = []))
  }

  let replied_to = data.replied_to;
  if (data.replied_to?.attachment_ids && data.replied_to.attachment_ids.length > 0) {
    const repliedAttachments = await Promise.all(
      data.replied_to.attachment_ids.map(async (id: string) => {
        const fileUrl = URL.createObjectURL((await api.getAttachment(id)).data);
        return fileUrl;
      })
    ).catch((err) => []);
    
    replied_to = {
      ...data.replied_to,
      attachment_ids: repliedAttachments
    };
  }

  const replacedMessage: IChatMessage = {
    ...data,
    attachment_ids: attachment_ids,
    replied_to: replied_to,
    status: status || 'sent',
  };

  const replacedChat = chatData.map((chat) =>
    chat.created_at === replacedMessage.created_at ? replacedMessage : chat
  );

  return replacedChat.find(({ created_at }) => created_at === replacedMessage.created_at)
    ? replacedChat
    : [...replacedChat, replacedMessage];
};

export const getViewsMessagesIds = (payload: { chat_id: string; chatData: chatDataType; email: string }) => {
  const { chatData, email, chat_id } = payload;

  const messages_id: string[] = [];
  const lastIndex = chatData[chat_id]?.data?.length - 1;

  if (chatData[chat_id]?.data[lastIndex]?.email === email) return messages_id;

  for (let idx = lastIndex; idx >= 0; idx--) {
    if (chatData[chat_id]?.data[idx]?.email === email) break;
    if (chatData[chat_id]?.data[idx]?.status === 'viewed') break;

    messages_id.push(chatData[chat_id].data[idx].id!);
  }

  return messages_id;
};

export const splitToGroup = (newChatData: IChatMessage[]) => {
  const splittedByGroups: IChatMessage[][] = [[]];
  let splittedIdx = 0;

  newChatData.forEach((chat, idx) => {
    splittedByGroups[splittedIdx].push(chat);
    if (!dateChecker(chat.created_at, newChatData[idx + 1]?.created_at || chat.created_at)) {
      splittedIdx += 1;
      splittedByGroups[splittedIdx] = [];
    }
  });

  return splittedByGroups;
};

const transformChatHistoryToChatData = async (
  data: any[],
  chat_id: string,
  room_id: string
): Promise<IChatMessage[]> => {
  return await Promise.all(
    data.map(async (data) => {
      let attachments = data?.attachment_ids;
      if (data.attachment_ids?.length > 0) {
        attachments = await Promise.all(
          data.attachment_ids.map(async (id: string) => {
            const fileUrl = URL.createObjectURL((await api.getAttachment(id)).data);
            return fileUrl;
          })
        ).catch((err) => (attachments = []));
      }

      let replied_to = data.replied_to;
      if (data.replied_to?.attachment_ids && data.replied_to.attachment_ids.length > 0) {
        const repliedAttachments = await Promise.all(
          data.replied_to.attachment_ids.map(async (id: string) => {
            const fileUrl = URL.createObjectURL((await api.getAttachment(id)).data);
            return fileUrl;
          })
        ).catch((err) => []);
        
        replied_to = {
          ...data.replied_to,
          attachment_ids: repliedAttachments
        };
      }

      return {
        ...data,
        type: 'chat',
        timestamp: data.created_at,
        user_name: data.author.name,
        message: data.content,
        email: data.author.email,
        created_at: data.created_at,
        room_id,
        chat_id,
        status: data.status || 'sent',
        attachment_ids: attachments,
        replied_to: replied_to,
      };
    })
  );
};

// const transformChatHistoryToChatData = (data: any[], chat_id: string, room_id: string): IChatMessage[] => {
//   return data.map((data) => ({
//     ...data,
//     type: 'chat',
//     timestamp: data.created_at,
//     user_name: data.author.name,
//     message: data.content,
//     email: data.author.email,
//     created_at: data.created_at,
//     room_id,
//     chat_id,
//     status: data.status || 'sent',
//   }));
// };

const chatTitleSelection = (chats: IChat[], ownerId: string, oponentName?: string): IChat[] => {
  return chats.map((chat) => {
    if (chat.entity_type === 2 || chat.entity_type === 3) {
      const opponentUser = chat.members.find(({ id }) => id !== ownerId);
      return {
        ...chat,
        entity_name: chat.members.length === 2 || oponentName ? oponentName || opponentUser?.name! : chat.entity_name,
        title: chat.members.length === 2 || oponentName ? opponentUser?.email! : chat.title,
      };
    }

    if (chat.entity_type === 1) {
      return {
        ...chat,
        entity_name: chat.room.name || chat.entity_name,
        title: chat.room.name || chat.entity_name,
      };
    }

    return chat;
  });
};

const createNewChat = async (payload: createChatPayloadType) => {
  const response = payload.isAI
    ? await aiAssistant.createChatAIAssistant({access_token: payload.data.entity_id!})
    : await api.createChat(payload.data);
  const transformedChat = chatTitleSelection([response.data], payload.ownerId, payload.oponentName)[0];
  return { chat: transformedChat, isForPush: true };
};

const createDocumentsChat = async (
  payload: createChatPayloadType,
  roomAllChats: IChat[],
  chatData: any,
  dispatch: any
) => {
  const sameChat = roomAllChats.find((chat) => chat.entity_id === payload.data.entity_id);
  const documentChat = !sameChat ? await createNewChat(payload).catch((err) => null) : null;
  const neededChatId = sameChat?.id || documentChat?.chat?.id;
  if (neededChatId && !chatData[neededChatId!]?.data.length) {    
    await dispatch(fetchChatHistory({ chat_id: neededChatId!, room_id: payload.data?.room_id! }));
  }
  return { chat: sameChat ? sameChat : documentChat?.chat!, isForPush: !sameChat };
};

const createAIChat = async (
  payload: createChatPayloadType,
  roomAllChats: IChat[],
  chatData: any,
  dispatch: any
) => {
  const aiChat = await createNewChat({...payload, isAI: true}).catch((err) => null);
  const sameChat = roomAllChats.find((chat) => chat.id === aiChat?.chat.id);
  
  const neededChatId = aiChat?.chat?.id || sameChat?.id;
  if (neededChatId && !chatData[neededChatId!]?.data.length) {
    await dispatch(fetchChatHistory({ chat_id: neededChatId, room_id: payload.data?.room_id!, isAI: true, access_token: String(payload.data.entity_id) }));
  }
  return { chat: sameChat ? sameChat : aiChat?.chat!, isForPush: !sameChat };
};

const createUserChat = async (payload: createChatPayloadType, roomAllChats: IChat[], chatData: any, dispatch: any) => {
  const sameChat = roomAllChats.find(
    (chat) =>
      chat.entity_type === 2 &&
      chat.members.length === 2 &&
      chat.members?.find(({ id }) => id === payload.data.members_ids[1])
  );

  if (payload.data.members_ids.length === 2 && sameChat) {
    const selectedChat = roomAllChats.find(({ id }) => id === sameChat.id);

    if (!chatData[sameChat.id].isLoadedHistory) {
      await dispatch(fetchChatHistory({ chat_id: sameChat.id, room_id: selectedChat?.room_id! }));
    }
    return { chat: selectedChat!, isForPush: false };
  }

  return await createNewChat(payload);
};

const chatsTypes = [createDocumentsChat, createNewChat, createUserChat, null, null, null, createAIChat];

const fetchChatData = createAsyncThunk(
  'chat/fetchData',
  async (payload: { data: IChatMessage; type: 'replace' | 'add' | 'cancel'; status?: messageStatusType }, thunkApi) => {
    const { dispatch } = thunkApi;
    const state = thunkApi.getState() as RootState;
    const { chatData, checkingMessages, selectedChat } = state.chat;
    const { selectedDocument } = state.documents;
    const { userData } = state.userData;
    const newChatData = await getCheckedChat(payload, chatData[payload.data.chat_id].data, payload.status);

    const splittedByGroups: IChatMessage[][] = splitToGroup(newChatData);    
    try {
      if (payload.type === 'add' && selectedDocument?.id) {
        dispatch(setSelectedDocument({
          ...selectedDocument, message_amount: (selectedDocument?.message_amount || 0) + 1,
        }))
      };
      if (payload.type === 'replace' && !payload.status) {
        dispatch(setAttemptCount(0));
        const filteredCheckingMessages = checkingMessages.filter(
          ({ created_at }) => created_at !== payload.data.created_at
        );
        dispatch(setCheckingMessages(filteredCheckingMessages));
        if (payload.data.email !== userData?.email && selectedChat?.id === payload.data.chat_id)
          dispatch(setViewsMessagesIds([payload.data.id!]));
      }
    } catch (Err) {
      console.log(Err, 'fetchChatData failed with error');
    }

    return {
      data: newChatData,
      grouped: splittedByGroups,
      chatId: payload.data.chat_id,
    };
  }
);

const fetchChatId = createAsyncThunk(
  'fetch/chatId',
  async (payload: { ownerId: string; roomId: string; chatId: null | string }, thunkApi) => {
    const { dispatch } = thunkApi;
    const abortController = new AbortController();
    dispatch(setUploadChatsController(abortController))

    try {
      const response = (await api.getRoomChats(payload.roomId, abortController.signal)).data
        .map((chat) => (chat.id === payload.chatId || chat.entity_type === 1 ? { ...chat } : chat))
        .sort((first, second) => (first.entity_type === 1 ? -1 : second.entity_type === 1 ? 1 : 0));

      const roomchatId = payload.chatId
        ? response.find(({ id }) => id === payload.chatId)!
        : response.find(({ entity_type }) => entity_type === 1)! || null;

      const transformedChats = chatTitleSelection(response, payload.ownerId);
      
      const sortedChats = sortChatsByLastMessage(transformedChats);
      
      if (roomchatId === null) return { chats: sortedChats, history: [], splittedHistory: [], selectedChat: null };

      return {
        chats: sortedChats,
        selectedChat: { ...roomchatId, unviewed_amount: 0 },
      };
    } catch (err) {
      console.log(err);
      return thunkApi.rejectWithValue('Download Failed');
    }
  }
);

const fetchGlobalChats = createAsyncThunk(
  'fetch/globalChats',
  async (payload: { ownerId: string; chatId: null | string }, thunkApi) => {
    try {
      const { dispatch } = thunkApi;
      const state = thunkApi.getState() as RootState;
      const { userData } = state.userData;
      const checkingMessages = JSON.parse(localStorage.getItem(userData?.email!) || '[]');
      
      const response = (await api.getGLobalChatsV2()).data
        .map((chat) => (chat.id === payload.chatId || chat.entity_type === 1 ? { ...chat, unviewed_amount: 0 } : chat))
        .sort((first, second) => (first.entity_type === 1 ? -1 : second.entity_type === 1 ? 1 : 0));
      
      const transformedChats = chatTitleSelection(response, payload.ownerId);
      const sortedChats = sortChatsByLastMessage(transformedChats);
  
      const selectedChat = payload.chatId ? transformedChats.find(chat => chat.id === payload.chatId) : null;
      
      selectedChat && dispatch(fetchSetSelectedChat(selectedChat));
      selectedChat && (await dispatch(fetchChatHistory({ room_id: selectedChat.room_id, chat_id: selectedChat.id })));
      
      return {
        chats: sortedChats,
        selectedChat: selectedChat ? { ...selectedChat, unviewed_amount: 0 } : null,
        checkingMessages,
      };
    } catch (err) {
      console.log(err);
      return thunkApi.rejectWithValue('Download Failed');
    }
  }
);

const createGLobalChat = createAsyncThunk('create/globalChat', async (payload: createChatPayloadType, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { globalChats, chatData } = state.chat;

    const sameChat = globalChats.find(
      (chat) => chat.entity_type === 3 && chat.members?.find(({ id }) => id === payload.data.members_ids[1])
    );
    
    if (sameChat) {
      if (!chatData[sameChat.id]?.isLoadedHistory) {
        await dispatch(fetchChatHistory({ chat_id: sameChat.id, room_id: sameChat?.room_id! }));
      }
      return { chat: sameChat!, isForPush: false };
    }
    const formData = new FormData();
    formData.append('user_id', payload.data.members_ids[1]);

    const response = await api.createGLobalChat(formData);
    const transformedChat = chatTitleSelection([response.data], payload.ownerId)[0];

    return { chat: transformedChat, isForPush: true };
  } catch (err) {
    return thunkApi.rejectWithValue('Failed to create global chat');
  }
});

const createChat = createAsyncThunk('fetch/createChat', async (payload: createChatPayloadType, thunkApi) => {
  try {
    const { dispatch } = thunkApi;
    const { roomAllChats, chatData } = (thunkApi.getState() as RootState).chat;
    const entityType = payload.data.entity_type;
    const handleEntity = chatsTypes[entityType];

    if (typeof handleEntity !== 'function') {
      console.warn(`No chat handler for entity type: ${entityType}`);
      return;
    }

    return await handleEntity(payload, roomAllChats, chatData, dispatch);
  } catch (err: any) {    
    return thunkApi.rejectWithValue('Download Failed');
  }
});

const fetchChatHistory = createAsyncThunk(
  'fetch/chatHistory',
  async ({ chat_id, room_id, page = 1, append = false, per_page = 50, isAI, access_token }: { room_id: string; chat_id: string; page?: number; append?: boolean; per_page?: number; offset?: number, isAI?: boolean, access_token?: string }, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { checkingMessages } = state.chat;
      const response = isAI
        ? await aiAssistant.getMessagesChatAIAssistant({ access_token: access_token!, page, per_page })
        : await api.getChatHistoryV2({ chat_id, page, per_page });
            
      const filteredChecks = checkingMessages.filter(
        ({ created_at }) => !response.data.messages.find((message) => message.created_at === created_at)
      );
      const chatMessageData = (await transformChatHistoryToChatData(response.data.messages, chat_id, room_id)).reverse();
      const splittedByGroups = splitToGroup(chatMessageData);
      dispatch(setCheckingMessages(filteredChecks));

      return {
        chat_id,
        history: chatMessageData,
        splittedHistory: splittedByGroups,
        append,
        hasMore: (page * per_page) < response.data.pagination.total,
        currentPage: page,
        totalMessages: response.data.pagination.total,
      };
    } catch (err: any) {
      console.log(err);
      return thunkApi.rejectWithValue('Download Failed');
    }
  }
);

const fetchChatInfo = createAsyncThunk(
  'fetch/chatInfo',
  async (payload: { chatId: string; ownerId: string }, thunkApi) => {
    try {
      const response = await api.getChatInfo(payload.chatId);
      const transformedChat = chatTitleSelection([response.data], payload.ownerId)[0];
      return transformedChat;
    } catch (Err) {
      return thunkApi.rejectWithValue('Failed to fetch chat info');
    }
  }
);

export const fetchUnviewedAmount = createAsyncThunk(
  'fetch/unviewedAmount',
  async ({ data, count }: { data: any; count: number }, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { documents, folders } = state.documents;
      const { selectedChat } = state.chat;
      const { available_rooms, userData } = state.userData;
      const { soundNotificationState } = state.windowState;

      const isMyMessage = data.email === userData?.email;

      dispatch(
        setDocuments({
          documents: documents.map((doc) => doc.id === data.entity_id
            ? {
              ...doc,
              unviewed_amount: count < 0
                ? 0
                : (count > 0 && !isMyMessage)
                  ? (Number(doc.unviewed_amount) || 0) + count
                  : doc.unviewed_amount,
              message_amount: count > 0
                ? (Number(doc.message_amount) || 0) + count
                : doc.message_amount,
              }
            : doc
          ),
          folders,
        })
      );

      if (!isMyMessage && selectedChat?.id !== (data.chat_id || data.id)) {
        dispatch(setUnviewedAmount({ count, chat_id: data.chat_id || data.id }));
        const roomWithsFreshData = available_rooms.map(room => room.id === data.room_id
          ? { ...room, unviewed_amount: (room?.unviewed_amount || 0) + 1 }
          : room
        );
        dispatch(setRooms(roomWithsFreshData));
      };

      if (count > 0 && !isMyMessage && !soundNotificationState.open) {
        if (!soundNotificationState.denied) {          
          soundNotificationState.enabled 
            ? dispatch(needPlayChatNotification(true))
            : dispatch(setSoundNotificationState({ open: true, enabled: false, denied: true }));
        }
      };
    } catch (Err) {
      return thunkApi.rejectWithValue('Failed to update document message counters');
    }
  }
);

export const setRejectMessages = createAsyncThunk(
  'chat/setRejecting',
  async (payload: { type?: StatusActions }, thunkApi) => {
    try {
      const rootState = thunkApi.getState() as RootState;
      const { chatData, checkingMessages, attemptCount } = rootState.chat;

      // const currentFilter = checkingMessages.filter(({ chat_id }) => chat_id === selectedChat?.id);
      // const rejectedChats = new Set(checkingMessages.map(({ chat_id }) => chat_id));
      const chatDataStore = JSON.parse(JSON.stringify(chatData)) as typeof chatData;
      let rejectedMessages: IChatMessage[] = checkingMessages.map((chat) => ({ ...chat, status: 'rejected' }));

      if (payload.type === StatusActions.REPLACE) {
        rejectedMessages.forEach((rejectedMessage) => {
          if (chatDataStore[rejectedMessage.chat_id]) {
            chatDataStore[rejectedMessage.chat_id].data = chatDataStore[rejectedMessage.chat_id].data.map((message) => {
              if (message.created_at === rejectedMessage.created_at) {
                rejectedMessages = rejectedMessages.filter(
                  ({ created_at }) => created_at !== rejectedMessage.created_at
                );
                return message;
              }

              return message;
            });
          }
        });

        rejectedMessages.forEach((rejectedMessage) => {
          if (chatDataStore[rejectedMessage.chat_id]) {
            chatDataStore[rejectedMessage.chat_id].data = [
              ...chatDataStore[rejectedMessage.chat_id].data,
              rejectedMessage,
            ].sort((a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix());

            chatDataStore[rejectedMessage.chat_id].grouped = splitToGroup(chatDataStore[rejectedMessage.chat_id].data);
          }
        });
      } else {
        rejectedMessages.forEach((rejectedMessage) => {
          if (chatDataStore[rejectedMessage.chat_id]) {
            chatDataStore[rejectedMessage.chat_id].data = chatDataStore[rejectedMessage.chat_id].data.map((message) =>
              message.created_at === rejectedMessage.created_at ? rejectedMessage : message
            );

            chatDataStore[rejectedMessage.chat_id].grouped = splitToGroup(chatDataStore[rejectedMessage.chat_id].data);
          }
        });
      }

      const roomRejectedMessages = new Set(
        rejectedMessages.filter(({ type }) => type === 'room').map(({ chat_id }) => chat_id)
      );
      const mainRejectedMessages = new Set(
        rejectedMessages.filter(({ type }) => type === 'main').map(({ chat_id }) => chat_id)
      );

      return {
        chatData: chatDataStore,
        allRejectedMessages: [Array.from(roomRejectedMessages), Array.from(mainRejectedMessages)],
        rejectedMessages,
        isShowSupport: rejectedMessages.length >= attemptCount && attemptCount >= 3,
      };
    } catch (Err) {
      return thunkApi.rejectWithValue('Failed to fetch chat info');
    }
  }
);

export const setMessagesToLocalStorage = createAsyncThunk('chat/setToLocalStorage', async (payload, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { checkingMessages } = state.chat;
    const { userData } = state.userData;

    localStorage.setItem(userData?.email!, JSON.stringify(checkingMessages));
  } catch (err) {
    console.log(err);
  }
});

export const fetchSetSelectedChat = createAsyncThunk('chat/fetchSetSelectedChat', async (payload: IChat, thunkApi) => {
  try {
    const { dispatch } = thunkApi;
    const state = thunkApi.getState() as RootState;
    const { chatData } = state.chat;
    const { userData, available_rooms } = state.userData;
    const { documents, folders, selectedDocument } = state.documents;

    dispatch(setSelectedChatRoom(payload));
    const messagesViews = getViewsMessagesIds({ chat_id: payload.id, chatData, email: userData?.email! });
        
    if (messagesViews.length) {
      dispatch(setViewsMessagesIds(messagesViews));
      dispatch(setRooms(available_rooms.map((room) => room.id === payload?.room_id
        ? { ...room, unviewed_amount: (room.unviewed_amount || 0) - messagesViews.length}
        : room
      )));
      if (payload.entity_id) {
        dispatch(setDocuments({
          documents: documents.map((doc) => doc.id === payload.entity_id
            ? {
              ...doc,
              unviewed_amount: 
                Number(doc.unviewed_amount) - messagesViews.length >= 0
                  ? Number(doc.unviewed_amount) - messagesViews.length
                  : 0
              }
            : doc
          ),
          folders,
        }));

        if (payload.entity_id === selectedDocument?.id) {
          dispatch(setSelectedDocument({
            ...selectedDocument,
            unviewed_amount: 
              Number(selectedDocument.unviewed_amount) - messagesViews.length >= 0
                ? Number(selectedDocument.unviewed_amount) - messagesViews.length
                : 0
          }))
        }
      }
    };
  } catch (err) {
    console.log(err, 'Error while fetchSetSelectedChat');
  }
});

export const switchChatMessagesStatus = createAsyncThunk(
  'chat/switchChatMessagesStatus',
  async (payload: IChatMessage[], thunkApi) => {
    try {
      const { dispatch } = thunkApi;
      const state = thunkApi.getState() as RootState;
      const { chatData, roomAllChats, globalChats } = state.chat;
      const chatDataStore = JSON.parse(JSON.stringify(chatData)) as typeof chatData;

      if (!payload.length) return chatDataStore;
      const chatId = payload[0].chat_id;
      const messageIds = payload.map(({ id }) => id);
      chatDataStore[chatId].data = chatDataStore[chatId].data.map((message) =>
        messageIds.includes(message.id) ? { ...message, status: 'viewed' } : message
      );
      chatDataStore[chatId].grouped = splitToGroup(chatDataStore[chatId].data);

      dispatch(setRoomAllChats(roomAllChats.map(roomChat => {
        const isUncludesLastMessageId = messageIds.includes(roomChat?.last_message?.id);        
        return isUncludesLastMessageId && roomChat?.last_message
          ? { ...roomChat, last_message: { ...roomChat.last_message!, status: 'viewed' } }
          : roomChat
      })));

      dispatch(setGlobalChats(globalChats.map(roomChat => {
        const isUncludesLastMessageId = messageIds.includes(roomChat?.last_message?.id);        
        return isUncludesLastMessageId && roomChat?.last_message
          ? { ...roomChat, last_message: { ...roomChat.last_message!, status: 'viewed' } }
          : roomChat
      })));

      dispatch(setChatData(chatDataStore));
      return chatDataStore;
    } catch (err) {
      console.log(err);
    }
  }
);

export const fetchAllTagsDocuments = createAsyncThunk(
  'chat/fetchAllTagsDocuments',
  async (payload: {room_id: string}, thunkApi) => {
    try {
      const response = await api.getAllDocumentsOfRoom(payload.room_id);      
      return response.data;
    } catch (err) {
      return thunkApi.rejectWithValue('Error fetchAllTagsDocuments');
    }
  }
);

export default fetchChatData;
export { createChat, createGLobalChat, fetchChatHistory, fetchChatId, fetchChatInfo, fetchGlobalChats };
